import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import { FaBars, FaTimes, FaInstagram, FaWhatsapp, FaTiktok, FaArrowLeft, FaMoneyBillWave, FaClock, FaShieldAlt, FaExclamationCircle, FaCheckCircle } from 'react-icons/fa';
import './HomePage.css';

const API_URL = window.location.hostname === "localhost" ? "http://localhost:5001" : "https://haus-of-aura-backend.onrender.com";

const categories = [
  {
    title: "Bridal Makeup", id: "bridal", services: [
      { name: "Bridal makeup package", image: '/images/Bridalmakeup.jpg' },
      { name: "Civil wedding", image: '/images/Civilwedding.jpg' },
      { name: "Church wedding", image: '/images/Churchwedding.jpg' },
      { name: "Engagement makeup", image: '/images/Engagementmakeup.jpg' },
      { name: "Bridal Shower makeup", image: '/images/BridalShowermakeup.jpg' },
      { name: "Bridesmaids", image: '/images/Bridesmaids.jpg' },
      { name: "Bridal makeup with touch up", image: '/images/Bridalmakeupwithtouchup.jpg' },
      { name: "Bridal Trial session", image: '/images/BridalTrial.jpg' },
    ],
  },
{
    title: "Studio Service Makeup", 
    id: "studio", 
    services: [
      { name: "Soft Glam", price: 80, image: '/images/look-soft-glam.jpg' },
      { name: "Full Glam", price: 100, image: '/images/look-full-glam.jpg' },
      { name: "Birthday Glam", price: 80, image: '/images/look-birthday.jpg' },
      { name: "Prom/Graduation", price: 80, image: '/images/look-prom.jpg' },
      { name: "Phone photos per outfit", price: 60, image: '/images/look-photoshoot.jpg' },
      { name: "Studio Glam", price: 80, image: '/images/StudioPhoto.jpg' },
    ],
  },
  {
    title: "Home Services", id: "home", services: [
      { name: "Home service makeup", price: 150, image: '/images/Homeservicemakeup.jpg' },
      { name: "Glasgow environment", price: 200, image: '/images/Scotlandenvironment.jpg' },
    ],
  },
  {
    title: "Training", id: "training", services: [
      { name: "Beginner makeup class", image: '/images/Beginnermakeupclass.jpg' },
      { name: "Upgrade makeup class", image: '/images/Upgrademakeupclass.jpg' },
      { name: "Private training", image: '/images/Privatetraining.jpg' },
      { name: "Lighting and editing class", image: '/images/Lightingandeditingclass.jpg' },
      { name: "Self Makeup lessons", image: '/images/SelfMakeuplessons.jpg' },
    ],
  },
];

const portfolioImages = ['/images/makeup1.jpg', '/images/makeup2.jpg', '/images/makeup3.jpg', '/images/makeup4.jpg', '/images/makeup5.jpg', '/images/makeup7.jpg', '/images/makeup8.jpg', '/images/makeup9.jpg'];

const HomePage = () => {
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [selectedImg, setSelectedImg] = useState(null);
  const [bookingStep, setBookingStep] = useState("idle");

  // Detailed Forms
  const [bridalForm, setBridalForm] = useState({ 
    name: "", 
    phone: "", 
    email: "", 
    weddingDate: "", 
    weddingType: "", // Added Field
    ceremonyTime: "", 
    readyLocation: "", 
    ceremonyVenue: "", 
    receptionVenue: "", 
    peopleCount: "Bride Only", 
    addServices: "", 
    trial: "No", 
    style: "Soft Glam", 
    concerns: "", 
    referral: "", 
    notes: "" 
  });
  
  const [trainingForm, setTrainingForm] = useState({ name: "", phone: "", email: "", location: "", interest: "Beginner Makeup Course", experience: "No", expDetail: "", goals: "", dates: "", paymentPlan: "No", referral: "" });
  
  const [standardBooking, setStandardBooking] = useState({ name: "", email: "", phone: "", dateTime: null, timeSlot: "" });

  const [bookingDetails, setBookingDetails] = useState({ specificService: "", price: "", serviceCategory: "" });
  const [availableSlots, setAvailableSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [sentStatus, setSentStatus] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const getCleanDate = (dateObj) => {
    if (!dateObj) return "";
    const y = dateObj.getFullYear();
    const m = String(dateObj.getMonth() + 1).padStart(2, '0');
    const d = String(dateObj.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  };

  useEffect(() => {
    if (standardBooking.dateTime && bookingDetails.serviceCategory && bookingStep === "dateTime") {
      const fetchSlots = async () => {
        setLoadingSlots(true);
        try {
          const dateStr = getCleanDate(standardBooking.dateTime);
          const res = await axios.get(`${API_URL}/api/bookings/slots?date=${dateStr}&serviceType=${encodeURIComponent(bookingDetails.serviceCategory)}`);
          setAvailableSlots(res.data);
        } catch (err) { console.error(err); }
        finally { setLoadingSlots(false); }
      };
      fetchSlots();
    }
  }, [standardBooking.dateTime, bookingDetails.serviceCategory, bookingStep]);

  const handleSelectService = (service, category) => {
    setSentStatus(false);
    setBookingDetails({ specificService: service.name, price: service.price ? `£${service.price}` : "", serviceCategory: category });
    if (category === "Bridal Makeup") setBookingStep("bridalForm");
    else if (category === "Training") setBookingStep("trainingForm");
    else setBookingStep("dateTime");
    setTimeout(() => navTo("booking-hub"), 100);
  };

  const submitInquiry = async (type, data) => {
    setProcessing(true);
    try {
      await axios.post(`${API_URL}/api/bookings/bridal-inquiry`, { ...data, type, specificService: bookingDetails.specificService });
      setSentStatus(true);
    } catch (err) { alert("Failed to send inquiry."); }
    finally { setProcessing(false); }
  };

  const handleStandardBooking = async (e) => {
    e.preventDefault();
    setProcessing(true);
    try {
      const dateStr = getCleanDate(standardBooking.dateTime);
      const res = await axios.post(`${API_URL}/api/bookings`, {
        date: dateStr, 
        timeSlot: standardBooking.timeSlot, 
        name: standardBooking.name, 
        email: standardBooking.email, 
        phone: standardBooking.phone, 
        specificService: bookingDetails.specificService, 
        serviceType: bookingDetails.serviceCategory, 
        price: bookingDetails.price
      });

      if (window.fbq) {
        window.fbq('track', 'Lead', {
          content_name: bookingDetails.specificService,
          value: parseFloat(bookingDetails.price.replace('£', '') || 0),
          currency: 'GBP'
        });
      }

      navigate('/confirmation', { state: { booking: res.data.booking } });
    } catch (err) { alert("Booking failed."); }
    finally { setProcessing(false); }
  };

  const navTo = (id) => { 
    setMenuOpen(false); 
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" }); 
  };

  return (
    <div className="haus-app-full">
      <header className={`site-nav-main ${scrolled ? 'is-scrolled' : ''}`}>
        <div className="nav-container">
          <div className="logo" onClick={() => navTo("hero")}>Haus Of Aura</div>
          <nav className="nav-links-desktop">
            <button onClick={() => navTo("services")}>Services</button>
            <button onClick={() => navTo("portfolio")}>Portfolio</button>
            <button onClick={() => navTo("booking-hub")}>Booking</button>
            <button onClick={() => navTo("terms")}>Policy</button>
            <button onClick={() => navTo("footer")}>Contact</button>
          </nav>
          <button className="nav-mobile-hamburger" onClick={() => setMenuOpen(true)}><FaBars /></button>
        </div>
      </header>

      <AnimatePresence>
        {menuOpen && (
          <motion.div className="mobile-full-menu" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
            <button className="mobile-close-btn" onClick={() => setMenuOpen(false)}><FaTimes /></button>
            <div className="mobile-links-list">
                  <button onClick={() => navTo("services")}>Services</button>
                  <button onClick={() => navTo("portfolio")}>Portfolio</button>
                  <button onClick={() => navTo("booking-hub")}>Booking Now</button>
                  <button onClick={() => navTo("terms")}>Our Policy</button>
                  <button onClick={() => navTo("footer")}>Contact</button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <section id="hero" className="hero-vid-wrap">
         <video className="hero-vid-src" autoPlay loop muted playsInline><source src="/hero-vid.mp4" type="video/mp4" /></video>
         <div className="hero-vid-overlay"></div>
         <div className="hero-vid-content"><button className="hero-vid-cta-btn" onClick={() => navTo("services")}>BOOK AN APPOINTMENT</button></div>
      </section>

      <section id="services" className="looks-catalog">
        <div className="looks-intro"><p className="overline">Catalog</p><h2 className="section-title">Choose your look</h2></div>
        {categories.map((cat, i) => (
          <div key={i} className="service-category-block">
             <div className="cat-title-divider"><h3>{cat.title}</h3><div className="divider-line"></div></div>
             <div className="services-image-grid">
                {cat.services.map((s, j) => (
                  <div key={j} className="service-image-card" onClick={() => handleSelectService(s, cat.title)}>
                     <div className="img-frame"><img src={process.env.PUBLIC_URL + s.image} alt={s.name} /></div>
                     <div className="card-details">
                        <div className="card-top-info"><h4>{s.name}</h4>{s.price && <span className="card-price">£{s.price}</span>}</div>
                        <button className="card-book-btn">{cat.id === 'bridal' || cat.id === 'training' ? 'Inquire' : 'Book Now'}</button>
                     </div>
                  </div>
                ))}
             </div>
          </div>
        ))}
      </section>

      <section id="portfolio" className="portfolio-section">
         <h2 className="section-title center">Portfolio</h2>
         <div className="marquee-wrapper"><div className="marquee-inner">
            {[...portfolioImages, ...portfolioImages].map((img, i) => (
              <div key={i} className="portfolio-marquee-item" onClick={() => setSelectedImg(img)}><img src={img} alt="Portfolio Work" /></div>
            ))}
         </div></div>
      </section>

      <section id="booking-hub" className="booking-hub-section">
         <div className="booking-ui-wrapper">
            {bookingStep === "idle" ? (
                <div className="booking-start-dashed"><div className="dashed-box-content"><h3>Your Aura Journey</h3><p>Select a service to start.</p></div></div>
            ) : sentStatus ? (
                <div className="booking-form-panel success-msg"><FaCheckCircle size={50} color="var(--gold)"/><p>Your inquiry for <b>{bookingDetails.specificService}</b> has been sent! We will contact you shortly.</p></div>
            ) : bookingStep === "bridalForm" ? (
                <form className="booking-form-panel" onSubmit={(e) => { e.preventDefault(); submitInquiry("Bridal", bridalForm); }}>
                    <h2 className="step-heading">Bridal Enquiry Form</h2>
                    <div className="form-fields-grid">
                        <div className="form-field-box"><label>Full Name</label><input required onChange={e => setBridalForm({...bridalForm, name: e.target.value})} /></div>
                        <div className="form-field-box"><label>Phone Number</label><input required onChange={e => setBridalForm({...bridalForm, phone: e.target.value})} /></div>
                        <div className="form-field-box"><label>Email Address</label><input required type="email" onChange={e => setBridalForm({...bridalForm, email: e.target.value})} /></div>
                        <div className="form-field-box"><label>Wedding Date</label><input required type="date" onChange={e => setBridalForm({...bridalForm, weddingDate: e.target.value})} /></div>
                        
                        {/* --- NEW WEDDING TYPE TEXT INPUT --- */}
                        <div className="form-field-box">
                            <label>Type of Wedding</label>
                            <input required placeholder="e.g. Traditional, White Wedding, etc." onChange={e => setBridalForm({...bridalForm, weddingType: e.target.value})} />
                        </div>

                        <div className="form-field-box"><label>Ceremony Time</label><input required placeholder="e.g. 1:00 PM" onChange={e => setBridalForm({...bridalForm, ceremonyTime: e.target.value})} /></div>
                        <div className="form-field-box"><label>Getting Ready Location</label><input required onChange={e => setBridalForm({...bridalForm, readyLocation: e.target.value})} /></div>
                        <div className="form-field-box"><label>Ceremony Venue</label><input required onChange={e => setBridalForm({...bridalForm, ceremonyVenue: e.target.value})} /></div>
                        <div className="form-field-box"><label>Reception Venue</label><input required onChange={e => setBridalForm({...bridalForm, receptionVenue: e.target.value})} /></div>
                        <div className="form-field-box"><label>People requiring makeup?</label><select onChange={e => setBridalForm({...bridalForm, peopleCount: e.target.value})}><option>Bride Only</option><option>Bride + Bridal Party</option></select></div>
                        <div className="form-field-box"><label>Number of Additional Services</label><input type="number" placeholder="0" onChange={e => setBridalForm({...bridalForm, addServices: e.target.value})} /></div>
                        <div className="form-field-box"><label>Bridal Trial?</label><select onChange={e => setBridalForm({...bridalForm, trial: e.target.value})}><option>No</option><option>Yes</option></select></div>
                        <div className="form-field-box"><label>Makeup Style</label><select onChange={e => setBridalForm({...bridalForm, style: e.target.value})}><option>Soft Glam</option><option>Natural Glam</option><option>Full Glam</option><option>Unsure</option></select></div>
                        <div className="form-field-box" style={{gridColumn:'1/-1'}}><label>Skin Concerns / Allergies</label><input onChange={e => setBridalForm({...bridalForm, concerns: e.target.value})} /></div>
                        <div className="form-field-box" style={{gridColumn:'1/-1'}}><label>How did you hear about us?</label><input onChange={e => setBridalForm({...bridalForm, referral: e.target.value})} /></div>
                        <div className="form-field-box" style={{gridColumn:'1/-1'}}><label>Additional Information</label><textarea onChange={e => setBridalForm({...bridalForm, notes: e.target.value})} /></div>
                    </div>
                    <div className="booking-panel-footer"><button type="submit" className="btn-gold-action" disabled={processing}>{processing ? 'Sending...' : 'Submit Bridal Enquiry'}</button></div>
                </form>
            ) : bookingStep === "trainingForm" ? (
                <form className="booking-form-panel" onSubmit={(e) => { e.preventDefault(); submitInquiry("Training", trainingForm); }}>
                    <h2 className="step-heading">Makeup Training Enquiry</h2>
                    <div className="form-fields-grid">
                        <div className="form-field-box"><label>Full Name</label><input required onChange={e => setTrainingForm({...trainingForm, name: e.target.value})} /></div>
                        <div className="form-field-box"><label>Phone Number</label><input required onChange={e => setTrainingForm({...trainingForm, phone: e.target.value})} /></div>
                        <div className="form-field-box"><label>Email Address</label><input required type="email" onChange={e => setTrainingForm({...trainingForm, email: e.target.value})} /></div>
                        <div className="form-field-box"><label>Location</label><input required onChange={e => setTrainingForm({...trainingForm, location: e.target.value})} /></div>
                        <div className="form-field-box"><label>Training Interest</label><select onChange={e => setTrainingForm({...trainingForm, interest: e.target.value})}><option>Beginner Makeup Course</option><option>Upgrade Makeup Course</option><option>Bridal Makeup Training</option><option>One-to-One Mentorship</option><option>Group Training</option></select></div>
                        <div className="form-field-box"><label>Experience?</label><select onChange={e => setTrainingForm({...trainingForm, experience: e.target.value})}><option>No</option><option>Yes</option></select></div>
                        <div className="form-field-box" style={{gridColumn:'1/-1'}}><label>Describe Experience</label><input onChange={e => setTrainingForm({...trainingForm, expDetail: e.target.value})} /></div>
                        <div className="form-field-box" style={{gridColumn:'1/-1'}}><label>What would you like to achieve?</label><input onChange={e => setTrainingForm({...trainingForm, goals: e.target.value})} /></div>
                        <div className="form-field-box"><label>Preferred Dates</label><input required onChange={e => setTrainingForm({...trainingForm, dates: e.target.value})} /></div>
                        <div className="form-field-box"><label>Payment Plan Required?</label><select onChange={e => setTrainingForm({...trainingForm, paymentPlan: e.target.value})}><option>No</option><option>Yes</option></select></div>
                        <div className="form-field-box" style={{gridColumn:'1/-1'}}><label>How did you hear about us?</label><input onChange={e => setTrainingForm({...trainingForm, referral: e.target.value})} /></div>
                    </div>
                    <div className="booking-panel-footer"><button type="submit" className="btn-gold-action" disabled={processing}>{processing ? 'Sending...' : 'Submit Training Enquiry'}</button></div>
                </form>
            ) : bookingStep === "dateTime" ? (
                <div className="booking-form-panel">
                    <h2 className="step-heading">1. Select Date & Time</h2>
                    <div className="booking-grid-flex">
                        <div className="calendar-container"><DatePicker selected={standardBooking.dateTime} onChange={d => setStandardBooking({...standardBooking, dateTime: d})} inline minDate={new Date()} /></div>
                        <div className="slots-container">
                            <label className="slots-label">Sessions Available</label>
                            <div className="slots-grid-pills" style={{gridTemplateColumns: '1fr'}}>
                                {loadingSlots ? <p>Checking...</p> : availableSlots.length > 0 ? availableSlots.map(s => (
                                    <button key={s._id} className={`slot-item ${standardBooking.timeSlot === s.timeSlot ? 'is-active' : ''}`} onClick={() => setStandardBooking({...standardBooking, timeSlot: s.timeSlot})}>{s.timeSlot}</button>
                                )) : <p>No slots for this date.</p>}
                            </div>
                        </div>
                    </div>
                    <div className="booking-panel-footer">
                        <button className="btn-link-back" onClick={() => setBookingStep("idle")}><FaArrowLeft /> Back</button>
                        <button className="btn-gold-action" disabled={!standardBooking.timeSlot} onClick={() => setBookingStep("details")}>Next Step</button>
                    </div>
                </div>
            ) : (
                <form className="booking-form-panel" onSubmit={handleStandardBooking}>
                    <h2 className="step-heading">2. Contact Details</h2>
                    <div className="form-fields-grid" style={{gridTemplateColumns: '1fr'}}>
                        <div className="form-field-box"><label>Full Name</label><input required placeholder="Enter full name" onChange={e => setStandardBooking({...standardBooking, name: e.target.value})} /></div>
                        <div className="form-field-box"><label>Email Address</label><input required type="email" placeholder="Enter email address" onChange={e => setStandardBooking({...standardBooking, email: e.target.value})} /></div>
                        <div className="form-field-box"><label>WhatsApp Number</label><input required placeholder="Enter WhatsApp number" onChange={e => setStandardBooking({...standardBooking, phone: e.target.value})} /></div>
                    </div>
                    <div className="booking-panel-footer">
                        <button type="button" className="btn-link-back" onClick={() => setBookingStep("dateTime")}><FaArrowLeft /> Back</button>
                        <button type="submit" className="btn-gold-action" disabled={processing}>Confirm & Secure Booking</button>
                    </div>
                </form>
            )}
         </div>
      </section>

      <section id="terms" className="policy-section">
         <div className="policy-container">
            <p className="overline">Guidelines</p>
            <h2 className="section-title">Our Policy</h2>
            <div className="policy-cards-grid">
               <div className="policy-card"><div className="policy-icon-circle"><FaMoneyBillWave /></div><h4>Booking Deposits</h4><p>A <b>30% non-refundable deposit</b> is required.</p></div>
               <div className="policy-card"><div className="policy-icon-circle"><FaClock /></div><h4>Lateness</h4><p>A late fee of <b>£10</b> applies for every 15 minutes delay.</p></div>
               <div className="policy-card"><div className="policy-icon-circle"><FaShieldAlt /></div><h4>Confirmation</h4><p>Booking confirmed once the deposit receipt has been received.</p></div>
               <div className="policy-card"><div className="policy-icon-circle"><FaExclamationCircle /></div><h4>Cancellation</h4><p>A <b>48-hour notice</b> is required. Cancellations within 48 hours result in loss of deposit.</p></div>
            </div>
         </div>
      </section>

      <footer id="footer" className="lux-site-footer">
         <div className="footer-wrap-box">
            <div className="footer-branding-title">Haus Of Aura</div>
            <div className="footer-navigation-links">
               <button onClick={() => navTo("services")}>Services</button>
               <button onClick={() => navTo("portfolio")}>Gallery</button>
               <button onClick={() => navTo("booking-hub")}>Book Now</button>
               <button onClick={() => navTo("terms")}>Policy</button>
               <button onClick={() => navTo("footer")}>Contact</button>
            </div>
            <div className="footer-social-icons">
               <a href="https://instagram.com/glamby.bamz" target="_blank" rel="noreferrer"><FaInstagram /></a>
               <a href="https://www.tiktok.com/@glambybamz" target="_blank" rel="noreferrer"><FaTiktok /></a>
               <a href="https://wa.me/447776504571" target="_blank" rel="noreferrer"><FaWhatsapp /></a>
            </div>
            <div className="footer-copyright-text">© 2026 Haus Of Aura. All rights reserved.</div>
         </div>
      </footer>

      <AnimatePresence>
        {selectedImg && (
          <motion.div className="lightbox-backdrop" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setSelectedImg(null)}>
             <motion.div className="lightbox-content" initial={{ scale: 0.8, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.8, y: 20 }} onClick={(e) => e.stopPropagation()}>
                <button className="lightbox-close-top" onClick={() => setSelectedImg(null)}><FaTimes /> CLOSE</button>
                <img src={process.env.PUBLIC_URL + selectedImg} alt="Portfolio Work" />
             </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default HomePage;